// src/components/policies/AncillaryPolicyCard.jsx
// Displays a single ancillary medical policy with HI coverage details

import React, { useState } from 'react';
import {
  Building2, ChevronDown, ChevronUp, Edit2, Trash2, DollarSign,
  Calendar, Check, Loader2, Stethoscope, Truck, Heart,
  Activity, Pill, Eye, Ear
} from 'lucide-react';
import { deleteAncillaryPolicy } from '../../api';
import AddAncillaryModal from './AddAncillaryModal';

const POLICY_TYPE_LABELS = {
  dental: 'Dental',
  vision: 'Vision',
  hearing: 'Hearing',
  hospital_indemnity: 'Hospital Indemnity',
  cancer: 'Cancer',
  accident: 'Accident',
  critical_illness: 'Critical Illness'
};

const POLICY_TYPE_ICONS = {
  dental: Stethoscope,
  vision: Eye,
  hearing: Ear,
  hospital_indemnity: Building2,
  cancer: Heart,
  accident: Activity,
  critical_illness: Pill
};

const STATUS_STYLES = {
  active: 'bg-green-100 text-green-700',
  expired: 'bg-red-100 text-red-700',
  lapsed: 'bg-orange-100 text-orange-700'
};

// Hospital Indemnity coverage labels
const HI_COVERAGE_LABELS = {
  daily_hospital: { label: 'Daily Hospital', suffix: '/day' },
  daily_hospital_days: { label: 'Days Covered', suffix: ' days', prefix: '' },
  icu_daily: { label: 'ICU Daily', suffix: '/day' },
  ambulance: { label: 'Ambulance', suffix: '' },
  outpatient_surgery: { label: 'Outpatient Surgery', suffix: '' },
  er_visit: { label: 'ER Visit', suffix: '' },
  cancer_rider: { label: 'Cancer Rider', suffix: '' },
  heart_attack: { label: 'Heart Attack', suffix: '' },
  stroke: { label: 'Stroke', suffix: '' },
  skilled_nursing: { label: 'Skilled Nursing', suffix: '/day' }
};

export default function AncillaryPolicyCard({ policy, clientId, onUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const Icon = POLICY_TYPE_ICONS[policy.policyType] || Building2;
  const isHospitalIndemnity = policy.policyType === 'hospital_indemnity';

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this policy?')) return;

    try {
      setDeleting(true);
      await deleteAncillaryPolicy(clientId, policy.id);
      onUpdate?.();
    } catch (err) {
      console.error('[AncillaryPolicyCard] Delete error:', err);
      alert('Failed to delete policy');
    } finally {
      setDeleting(false);
    }
  };

  // Get active HI coverages
  const getActiveCoverages = () => {
    if (!policy.hiCoverages) return [];
    return Object.entries(policy.hiCoverages)
      .filter(([key, value]) => value && value > 0)
      .map(([key, value]) => ({
        key,
        value,
        ...HI_COVERAGE_LABELS[key]
      }));
  };

  const activeCoverages = getActiveCoverages();

  return (
    <>
      <div className="border border-slate-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow">
        {/* Header */}
        <div
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 transition-colors"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Icon className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="font-medium text-slate-800">
                  {POLICY_TYPE_LABELS[policy.policyType] || policy.policyType}
                </h4>
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_STYLES[policy.status] || 'bg-slate-100 text-slate-600'}`}>
                  {policy.status}
                </span>
              </div>
              <p className="text-sm text-slate-500">
                {policy.carrier}
                {policy.policyNumber && ` • ${policy.policyNumber}`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="font-semibold text-slate-800">{formatCurrency(policy.monthlyPremium)}/mo</p>
              {isHospitalIndemnity && activeCoverages.length > 0 && (
                <p className="text-xs text-slate-500">{activeCoverages.length} coverages</p>
              )}
            </div>
            {expanded ? (
              <ChevronUp className="w-5 h-5 text-slate-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-400" />
            )}
          </div>
        </div>

        {/* Expanded Details */}
        {expanded && (
          <div className="border-t border-slate-100 p-4 bg-slate-50">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
              <div>
                <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1">
                  <Building2 className="w-3.5 h-3.5" />
                  Carrier
                </div>
                <p className="font-medium text-slate-800">{policy.carrier}</p>
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1">
                  <DollarSign className="w-3.5 h-3.5" />
                  Premium
                </div>
                <p className="font-medium text-slate-800">{formatCurrency(policy.monthlyPremium)}/mo</p>
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1">
                  <Calendar className="w-3.5 h-3.5" />
                  Effective
                </div>
                <p className="font-medium text-slate-800">{formatDate(policy.effectiveDate)}</p>
              </div>
            </div>

            {/* Hospital Indemnity Coverage Details */}
            {isHospitalIndemnity && activeCoverages.length > 0 && (
              <div className="mb-4">
                <h5 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <Truck className="w-4 h-4 text-blue-500" />
                  Coverage Details
                </h5>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {activeCoverages.map(({ key, value, label, suffix, prefix }) => (
                    <div
                      key={key}
                      className="flex items-center gap-2 p-2.5 bg-white rounded-lg border border-slate-200"
                    >
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs text-slate-500 truncate">{label}</p>
                        <p className="font-medium text-slate-800 text-sm">
                          {prefix !== '' && '$'}{value.toLocaleString()}{suffix}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {policy.notes && (
              <div className="mb-4 p-3 bg-white rounded-lg border border-slate-200">
                <p className="text-sm text-slate-600">{policy.notes}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setShowEdit(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <Edit2 className="w-4 h-4" />
                Edit
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
              >
                {deleting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                Delete
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <AddAncillaryModal
        isOpen={showEdit}
        onClose={() => setShowEdit(false)}
        clientId={clientId}
        policy={policy}
        onSuccess={() => {
          setShowEdit(false);
          onUpdate?.();
        }}
      />
    </>
  );
}
